export type CatalogModifierOptionProjection = {
  modifier_option_id: string;
  code: string;
  name: string;
  is_default?: boolean;
  sort_order?: number;
};

export type CatalogModifierGroupProjection = {
  modifier_group_id: string;
  code: string;
  name: string;
  min_select: number;
  max_select: number;
  allow_option_quantity: boolean;
  max_option_quantity: number;
  sort_order?: number;
  options: CatalogModifierOptionProjection[];
};

export type CatalogVariantOptionProjection = {
  dimension_id: string;
  dimension_code?: string;
  dimension_name: string;
  option_id: string;
  option_code?: string;
  option_name: string;
  sort_order?: number;
};

export type CatalogSelectionDimensionProjection = {
  dimension_id: string;
  dimension_code?: string;
  dimension_name: string;
  selection_stage: number;
  sort_order: number;
  display_order?: number | null;
  is_primary_group?: boolean;
};

export type CatalogVariantProjection = {
  variant_id: string;
  variant_code: string;
  variant_label: string;
  sort_order?: number;
  options?: CatalogVariantOptionProjection[];
  modifiers?: CatalogModifierGroupProjection[];
};

export type CatalogProductMediaDeliveryRef = {
  scheme: "CATALOG_MEDIA_ASSET_V1";
  media_asset_id: string;
  content_revision: string;
};

export type CatalogProductMediaProjection = {
  media_asset_id: string;
  media_role: "PRIMARY" | "GALLERY";
  sort_order: number;
  fit_mode: "COVER" | "CONTAIN";
  focal_point?: { x: number; y: number } | null;
  alt_text?: string | null;
  mime_type: string;
  byte_size: number;
  width_px?: number | null;
  height_px?: number | null;
  content_revision: string;
  delivery_ref: CatalogProductMediaDeliveryRef;
};

export type CatalogProductMediaSnapshot = {
  schema: "CATALOG_PRODUCT_MEDIA_V1";
  primary_media: CatalogProductMediaProjection | null;
  media: CatalogProductMediaProjection[];
};

export type CatalogMenuSnapshot = {
  schema?: string;
  menu: {
    item_id: string;
    item_code: string;
    name: string;
    card_display_name?: string | null;
    effective_card_display_name?: string | null;
    sale_card_presentation_mode?: string | null;
    status?: string;
    menu_category_id?: string | null;
    category_code?: string | null;
    category_name?: string | null;
    development_stage?: string | null;
  };
  variants: CatalogVariantProjection[];
  selection_dimensions?: CatalogSelectionDimensionProjection[];
  distribution?: unknown;
  product_media?: CatalogProductMediaSnapshot;
};

export type CatalogRuntimeEvent = {
  revision: number;
  runtime_action: "UPSERT" | "REMOVE";
  menu_item_id: string;
  snapshot: CatalogMenuSnapshot | null;
};

export type CatalogRuntimePayload = {
  ok: boolean;
  contract: string;
  projection_contract?: string;
  mode: "FULL" | "DELTA";
  organization_id: string;
  branch_id: string;
  node_id?: string;
  since_revision: number;
  latest_revision: number;
  branch_operational: boolean;
  returned_event_count: number;
  total_event_count: number;
  has_more: boolean;
  events: CatalogRuntimeEvent[];
};

export type PriceSnapshot = {
  ready: boolean;
  branch_id: string;
  final_price: number;
  effective_to: string | null;
  menu_item_id: string;
  currency_code: string;
  currency_scale: number | null;
  effective_from: string;
  menu_variant_id: string | null;
  price_version_id: string;
  price_version_no: number;
  sales_channel_id: string | null;
  source_branch_id: string | null;
  source_sales_channel_id: string | null;
};

export type PriceRuntimeEvent = {
  cursor?: RuntimeCursor;
  runtime_action: "UPSERT" | "REMOVE";
  menu_item_id: string;
  menu_variant_id: string | null;
  event_kind?: string;
  snapshot: PriceSnapshot | null;
};

export type ModifierPriceSnapshot = {
  ok: boolean;
  ready: boolean;
  branch_id: string;
  modifier_option_id: string;
  modifier_group_id: string;
  option_name: string;
  option_status: string;
  price_delta: number;
  currency_code: string;
  price_version_id: string;
  price_version_no: number;
  effective_from: string;
  effective_to: string | null;
  sales_channel_id: string | null;
  pricing_scope?: string;
  sort_order?: number;
};

export type ModifierPriceRuntimeEvent = {
  cursor?: RuntimeCursor;
  runtime_action: "UPSERT" | "REMOVE";
  modifier_option_id: string;
  event_kind?: string;
  snapshot: ModifierPriceSnapshot | null;
};

export type RuntimeCursor = {
  effective_at: string;
  revision: number;
};

export type PriceCursor = RuntimeCursor;

export type PriceRuntimePayload = {
  ok: boolean;
  contract: string;
  projection_contract?: string;
  mode: "FULL" | "DELTA";
  organization_id: string;
  branch_id: string;
  sales_channel_id: string | null;
  node_id?: string;
  server_at: string;
  next_cursor: RuntimeCursor;
  event_count?: number;
  returned_event_count?: number;
  has_more?: boolean;
  events: PriceRuntimeEvent[];
};

export type ModifierPriceRuntimePayload = {
  ok: boolean;
  contract: string;
  projection_contract?: string;
  mode: "FULL" | "DELTA";
  organization_id: string;
  branch_id: string;
  sales_channel_id: string | null;
  node_id?: string;
  server_at: string;
  next_cursor: RuntimeCursor;
  event_count?: number;
  returned_event_count?: number;
  has_more?: boolean;
  events: ModifierPriceRuntimeEvent[];
};

type GatewayEnvelope<T> = {
  ok: boolean;
  data?: T;
  error?: string;
};

export type RuntimeGatewayResult<T> = {
  ok: boolean;
  data: T | null;
  error: string | null;
};

type ClientOptions = {
  baseUrl: string;
  timeoutMs?: number;
};

export class PosCatalogPriceGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: ClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 12_000;
  }

  catalog(input: {
    deviceSecret: string;
    sessionToken: string;
    sinceRevision: number;
    limit?: number;
  }) {
    return this.post<CatalogRuntimePayload>("/api/pos/runtime/catalog", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      since_revision: input.sinceRevision,
      limit: input.limit ?? 500,
    });
  }

  price(input: {
    deviceSecret: string;
    sessionToken: string;
    cursor: RuntimeCursor | null;
    limit?: number;
  }) {
    return this.post<PriceRuntimePayload>("/api/pos/runtime/price", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      cursor: input.cursor,
      limit: input.limit ?? 1000,
    });
  }

  modifierPrice(input: {
    deviceSecret: string;
    sessionToken: string;
    cursor: RuntimeCursor | null;
    limit?: number;
  }) {
    return this.post<ModifierPriceRuntimePayload>("/api/pos/runtime/modifier-price", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      cursor: input.cursor,
      limit: input.limit ?? 1000,
    });
  }

  private async post<T>(path: string, payload: Record<string, unknown>): Promise<RuntimeGatewayResult<T>> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const envelope = (await response.json()) as GatewayEnvelope<T>;
      if (!response.ok || !envelope.ok || !envelope.data) {
        return { ok: false, data: null, error: envelope.error ?? `HTTP_${response.status}` };
      }
      return { ok: true, data: envelope.data, error: null };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError"
          ? "GATEWAY_TIMEOUT"
          : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
